import React, { useContext } from "react"
import { View, ScrollView, StyleSheet, Text } from "react-native"
import { useRoute } from "@react-navigation/native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import BrevetFunnelHeader from "./components/BrevetFunnelHeader"
import { useBrevetFunnel } from "./context/BrevetFunnelContext"
import { BREVET_FORMULAS, resolveFormula } from "./constants/brevetFormulas"
import {
    getSlotById,
    formatFirstSessionLabel,
    getFormulaEconomics,
} from "./constants/brevetSlots"
import { formatEuros } from "./utils/formatEuros"

const BrevetConfirmationScreen = ({ navigation }) => {
    const dimensions = useContext(DimensionsContext)
    const { state, reset } = useBrevetFunnel()
    const route = useRoute()

    // Stripe redirects to the success URL with formula_id + slot_id as query
    // params; that's the most reliable source on this screen (the cross-domain
    // redirect from Stripe can wipe the Context if the user lands on a fresh
    // origin). Fall back to the Context for native deep-link or no-redirect runs.
    const slotId = route.params?.slot_id || state.selectedSlotId
    const formulaId = route.params?.formula_id || state.selectedFormulaId

    // Récap post-paiement : overlay des économies de la phase active pour que le
    // prix / les heures affichés matchent ce qui vient d'être payé. `resolveFormula`
    // retombe déjà sur les défauts statiques de `BREVET_FORMULAS[formulaId]` quand
    // l'éco est null (phase expirée entre paiement et affichage) ; il ne rend null
    // que si `formulaId` est inconnu → fallback final sur `sprint`.
    const formula =
        resolveFormula(formulaId, getFormulaEconomics(formulaId)) ||
        BREVET_FORMULAS.sprint
    const slot = getSlotById(slotId)
    const firstname = state.studentFirstname || "votre enfant"

    const firstSessionLabel = slot ? formatFirstSessionLabel(slot) : "à venir"

    const referralActive = !!state.referralCode
    const displayedPrice = referralActive ? formula.price / 2 : formula.price
    const displayedRemainder = referralActive
        ? Math.max(0, displayedPrice - formula.deposit)
        : formula.remainder

    const containerWidth = Math.min(dimensions?.width || 400, 460)

    const handleRestart = () => {
        reset()
        navigation.navigate("brevet/start")
    }

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader
                    step={4}
                    total={4}
                    hideBack
                    hideStep
                    onBack={() => {}}
                />
                <ScrollView
                    contentContainerStyle={styles.content}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.successIcon}>
                        <FontAwesome5
                            name="check"
                            size={36}
                            color={theme.colors.success}
                        />
                    </View>

                    <Text style={styles.title}>C'est confirmé 🎉</Text>
                    <Text style={styles.subtitle}>
                        L'inscription de {firstname} au Sprint Brevet est validée.
                    </Text>
                    <Text style={styles.emailHint}>
                        📧 Un email de confirmation vient de vous être envoyé — vérifiez votre boîte de réception.
                    </Text>

                    {/* Infos pratiques premier cours */}
                    <View style={styles.block}>
                        <Text style={styles.blockKicker}>Prochain cours</Text>
                        <Text style={styles.blockMain}>{firstSessionLabel}</Text>

                        <View style={styles.infoRow}>
                            <FontAwesome5
                                name="video"
                                size={12}
                                color={theme.colors.primary}
                            />
                            <Text style={styles.infoText}>
                                100 % en ligne en visio sur l'app Masteur.
                            </Text>
                        </View>
                        <View style={styles.infoRow}>
                            <FontAwesome5
                                name="mobile-alt"
                                size={12}
                                color={theme.colors.primary}
                            />
                            <Text style={styles.infoText}>
                                Lien d'accès envoyé par email la veille du cours.
                            </Text>
                        </View>
                        <View style={styles.infoRow}>
                            <FontAwesome5
                                name="user-friends"
                                size={12}
                                color={theme.colors.primary}
                            />
                            <Text style={styles.infoText}>
                                Tuteur attribué sous 48 h — on fait le tri parmi 10 000 candidats.
                            </Text>
                        </View>
                    </View>

                    {/* Récap léger — labels alignés flex:1 / value droite, comme Pricing */}
                    <View style={styles.summary}>
                        <SummaryRow
                            label="Formule"
                            value={`${formula.label} ${formula.hours}h`}
                        />
                        <SummaryRow
                            label="Payé aujourd'hui"
                            value={formatEuros(formula.deposit)}
                        />
                        <SummaryRow
                            label={"Prélèvement automatique\n(après le 1er cours)"}
                            value={formatEuros(displayedRemainder)}
                            isLast
                        />
                    </View>
                </ScrollView>
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Continuer"
                        onPress={() => navigation.navigate("brevet/app-download")}
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                    {__DEV__ && (
                        <FilledButtonM
                            label="Recommencer (dev only)"
                            mode="text"
                            color={theme.colors.light}
                            onPress={handleRestart}
                            style={{ width: "100%", marginTop: 8, marginBottom: 0 }}
                        />
                    )}
                </View>
            </View>
        </View>
    )
}

const SummaryRow = ({ label, value, isLast }) => (
    <View
        style={[
            styles.summaryRow,
            isLast && { borderBottomWidth: 0, paddingBottom: 0 },
        ]}
    >
        <Text style={[styles.summaryLabel, { flex: 1 }]}>{label}</Text>
        <Text style={styles.summaryValue}>{value}</Text>
    </View>
)

const styles = StyleSheet.create({
    root: {
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.whiteAlmost,
    },
    frame: {
        flex: 1,
        backgroundColor: theme.colors.white,
        paddingHorizontal: 20,
    },
    content: {
        paddingTop: 18,
        paddingBottom: 24,
    },
    successIcon: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: theme.colors.successLight,
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "center",
        marginBottom: 12,
    },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 22,
        color: theme.colors.primary,
        textAlign: "center",
        marginBottom: 4,
    },
    subtitle: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        textAlign: "center",
        marginBottom: 10,
    },
    emailHint: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12,
        color: theme.colors.primary,
        textAlign: "center",
        marginBottom: 18,
        lineHeight: 17,
    },
    block: {
        backgroundColor: theme.colors.primaryLighter,
        padding: 14,
        borderRadius: 12,
        marginBottom: 14,
    },
    blockKicker: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        color: theme.colors.primary,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    blockMain: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 15,
        color: theme.colors.primary,
        marginBottom: 10,
    },
    infoRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: 6,
    },
    infoText: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        lineHeight: 17,
    },
    summary: {
        marginTop: 4,
        paddingHorizontal: 4,
    },
    summaryRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 9,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lightMore,
    },
    summaryLabel: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
    },
    summaryValue: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.grey,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default BrevetConfirmationScreen
